import Add16 from '@carbon/icons-react/es/add/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import Information20 from '@carbon/icons-react/es/information/20';
import Redo16 from '@carbon/icons-react/es/redo/16';
import moment, { Moment } from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { PermissionSetType, User, UserId } from 'api/models';
import { TimesheetEntryType } from 'api/timesheets';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { Button } from 'components/atoms/button';
import { humanise } from 'components/atoms/humanised-date';
import { Modal } from 'components/atoms/modal';
import { TableSkeleton } from 'components/atoms/table';
import { useHeaderHeight } from 'components/molecules/page';
import { ApprovedSeal } from 'components/organisms/timesheets/common/approved-seal';
import { EmptyState as EmptyTimesheet } from 'components/organisms/timesheets/common/empty-state';
import { ProjectOption } from 'components/organisms/timesheets/common/project-option';
import {
  BlueGhostButton,
  BodyWrapper as TimesheetBodyWrapper,
  Footer,
  FooterActionRow,
  Wrapper,
} from 'components/organisms/timesheets/common/style';
import { TitleSelect } from 'components/organisms/timesheets/common/title-select';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useQueryParams } from 'hooks/use-query-params';
import { BodyShort01 } from 'ozone/atoms';
import {
  AddAdHocDrawer,
  AddTasksDrawer,
  CalenderDrawer,
  EditAdHocDrawer,
  useProjectOptionsForTimesheet,
  RejectCommentDrawer,
} from '../drawers';
import { useTimesheetActions } from '../timesheets-actions';
import {
  RejectNoteType,
  TimesheetAdhocTask,
  TimesheetStatus,
  TimesheetTask,
  TimesheetWeeklyRow,
} from '../types';
// eslint-disable-next-line import/no-internal-modules
import { FooterTable } from '../week-view-table/footer-table';
// eslint-disable-next-line import/no-internal-modules
import { WeekViewTable } from '../week-view-table/week-view-table';
import { DrawerType } from './constants';
import { SubmitTimesheetButton } from './submit-button';

export const StyledDate = styled.span`
  font-weight: var(--heading-8-weight);
  font-size: var(--font-size-sm);
`;

const BodyShort01Styled = styled(BodyShort01)`
  color: var(--gray-70);
`;

const RejectInfoIcon = styled(Information20)`
  color: var(--ozone-red-background) !important;
`;

export const FreezeInfoIcon = styled(Information20)`
  color: var(--blue-60) !important;
`;

export const Message = styled.div`
  ${BodyShort01};
  display: flex;
  gap: var(--spacing-05);
  align-items: center;

  ${RejectInfoIcon} {
    color: var(--warning-border-color);
  }
`;

const RejectInfoWrapper = styled.div`
  background-color: var(--ozone-red-surface);
  border: 1px solid var(--red-50);
  border-left-width: var(--spacing-02);
  padding: var(--spacing-02) var(--spacing-05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--input-size-sm);
  width: 400px;
`;

export const FreezeInfoWrapper = styled.div<{ allUserTable?: boolean }>`
  background-color: var(--blue-10);
  border: 1px solid var(--blue-30);
  border-left-width: var(--spacing-02);
  padding: var(--spacing-03);
  margin: ${({ allUserTable }) => allUserTable && '0 var(--layout-01)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--input-size-sm);
  width: ${({ allUserTable }) =>
    allUserTable ? 'calc(100% - calc(2 * var(--layout-01)))' : '100%'};
`;

const ActionRow = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  position: relative;

  &:after {
    content: '';
    left: 0;
    top: -1px;
    border-top: 1px solid var(--border-color);
    position: absolute;
    right: 0;
  }
`;

const StyledFooterActionRow = styled(FooterActionRow)`
  justify-content: flex-start;

  > :first-child {
    flex-grow: 1;
  }

  ${BlueGhostButton} {
    margin-left: 0;
  }
`;

const BodyWrapper = styled(TimesheetBodyWrapper)`
  overflow-y: auto;
`;

export const RejectInfo = () => (
  <RejectInfoWrapper>
    <Message>
      <RejectInfoIcon />
      <span>
        <FormattedMessage id="timesheets.text.reject.info" />
      </span>
    </Message>
  </RejectInfoWrapper>
);

interface FreezeInfoNoteType {
  isWeekFreezedAfter?: string;
  allUserTable?: boolean;
}

export const FreezeInfoNote = ({
  isWeekFreezedAfter,
  allUserTable,
}: FreezeInfoNoteType) => (
  <FreezeInfoWrapper allUserTable={allUserTable}>
    <Message>
      <FreezeInfoIcon />
      <span>
        <FormattedMessage
          id="timesheets.text.freeze.info"
          values={{
            date: (
              <StyledDate>{humanise(moment(isWeekFreezedAfter))}</StyledDate>
            ),
          }}
        />
      </span>
    </Message>
  </FreezeInfoWrapper>
);

const AddProjectActionRow = ({ onSelection, projectOptions }) => (
  <ActionRow>
    <TitleSelect
      triggerComponent={() => (
        <Wrapper>
          <Button
            data-cy="timesheets.buttons.add_project"
            type="link"
            icon={<Add16 />}
            iconPlacement="left"
          >
            <FormattedMessage id="timesheets.buttons.add_project" />
          </Button>
        </Wrapper>
      )}
      onChange={onSelection}
      options={projectOptions}
      components={{
        Option: ProjectOption,
      }}
      selected={{ value: -1, label: 'default' }}
      noOptionsMessage={() => (
        <FormattedMessage id="projects.no_results.empty.title" />
      )}
      selector="timesheet-project-select"
    />
  </ActionRow>
);

const WithdrawAllButtonWithModal = ({
  isStatusChangeInProgress,
  setConfirmationPopupVisible,
  unsubmitTimesheet,
  confirmationPopupVisible,
}) => (
  <>
    <Button
      data-cy="timesheets.buttons.withdraw_from_approval"
      icon={<Redo16 />}
      iconPlacement="right"
      danger
      loading={isStatusChangeInProgress}
      onClick={() => setConfirmationPopupVisible(true)}
    >
      <FormattedMessage id="timesheets.buttons.withdraw_from_approval" />
    </Button>
    <Modal
      visible={confirmationPopupVisible}
      title={<FormattedMessage id="timesheets.text.withdraw_approval" />}
      confirmLoading={false}
      okType="danger"
      okText={<FormattedMessage id="timesheets.buttons.withdraw" />}
      onCancel={() => setConfirmationPopupVisible(false)}
      onOk={() => {
        setConfirmationPopupVisible(false);
        unsubmitTimesheet();
      }}
    >
      <p>
        <FormattedMessage id="timesheets.text.withdraw_approval_content_owner" />
      </p>
    </Modal>
  </>
);

interface Props {
  startDate: Moment;
  configuration: Configuration;
  currentUser: User;
  visibleDrawer: DrawerType | undefined;
  isWeekFreezed: boolean;
  isProjectOwnerApproval: boolean;
  rejectNotes?: RejectNoteType[];
  setVisibleDrawer: (DrawerType: DrawerType | undefined) => void;
}

export const MyTimesheet: FC<Props> = ({
  startDate,
  configuration,
  currentUser,
  visibleDrawer,
  isWeekFreezed,
  rejectNotes,
  isProjectOwnerApproval,
  setVisibleDrawer,
}) => {
  const [editDetails, setEditDetails] = useState<TimesheetWeeklyRow>();
  const query = useQueryParams();
  const isCalenderAuthRedirected = query.get('success');

  useEffect(() => {
    if (isCalenderAuthRedirected) {
      setVisibleDrawer(DrawerType.Integration);
    }
  }, [isCalenderAuthRedirected, setVisibleDrawer]);
  const [confirmationPopupVisible, setConfirmationPopupVisible] =
    useState(false);
  const { pathname } = useLocation();

  const isWeekFreezedAfter = useMemo(
    () => configuration.lockSetting.freeze.enableAfter,
    [configuration.lockSetting.freeze.enableAfter],
  );

  const isCurrentWeek = pathname.includes('this-week');
  const IS_MY_TIMESHEET = true;
  const {
    projects,
    timesheet,
    isStatusChangeInProgress,
    timesheetWeekRows,
    timeEntryCategories,
    isTimesheetLoading,
    timeSheetSummary,
    timesheetEntries,
    addTasksToTimesheet,
    getPreviousWeekTasks,
    addAdhocActivitiesToTimesheet,
    updateTimeEntryInTask,
    addTimeEntryInTask,
    deleteTimeEntryInTask,
    deleteTask,
    unsubmitTask,
    editAdhocTaskName,
    submitTimesheet,
    unsubmitTimesheet,
    addIntegrationTaskToTimesheet,
    addProjectToTimesheet,
    addEntityToProject,
    changeProject,
    addActivityToProject,
  } = useTimesheetActions(startDate, currentUser, IS_MY_TIMESHEET);

  const projectOptions = useProjectOptionsForTimesheet(projects);

  const userPermissionType = useUserPermissionSetType();
  const isUserAdmin = userPermissionType === PermissionSetType.AccountAdmin;

  const PAGE_HEADER_HEIGHT = useHeaderHeight();
  const EXTRA_SPACE = 4;
  const RIGHT_COMMENT_SPACE = 160;
  const isInEditable =
    (timesheet?.status !== TimesheetStatus.Approved ||
      isProjectOwnerApproval) &&
    !(isWeekFreezed && !isUserAdmin);

  const alreadyAddedAdhocTasks = useMemo(
    () =>
      (timesheetWeekRows ?? [])
        .filter((x) => x.task.entryType === TimesheetEntryType.AdhocEntry)
        .map((x) => x.task as TimesheetAdhocTask),
    [timesheetWeekRows],
  );
  const alreadyAddedTasks = useMemo(
    () =>
      (timesheetWeekRows ?? [])
        .filter((x) => x.task.entryType === TimesheetEntryType.TaskEntry)
        .map((x) => x.task as TimesheetTask),
    [timesheetWeekRows],
  );

  const scrollToLastRow = useCallback(() => {
    setTimeout(() => {
      const tableLastRow = document.querySelector('.scrollToView');

      tableLastRow?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }, 500);
  }, []);

  const onSelection = useCallback(
    (props) => {
      addProjectToTimesheet(props);
      scrollToLastRow();
    },
    [addProjectToTimesheet, scrollToLastRow],
  );

  const addPreviousWeektasks = useCallback(() => {
    getPreviousWeekTasks();
    scrollToLastRow();
  }, [getPreviousWeekTasks, scrollToLastRow]);

  const submitTimesheetCondition = useMemo(() => {
    if (isProjectOwnerApproval && timesheetWeekRows) {
      for (let i = 0; i < timesheetWeekRows?.length; i += 1) {
        if (
          timesheetWeekRows[i].task.approvalStatus &&
          timesheetWeekRows[i].task.approvalStatus ===
            TimesheetStatus.NotSubmitted
        ) {
          return true;
        }
        if (!timesheetWeekRows[i].task.approvalStatus) {
          return true;
        }
      }

      return false;
    }

    return timesheet?.status === TimesheetStatus.NotSubmitted;
  }, [isProjectOwnerApproval, timesheet?.status, timesheetWeekRows]);

  return (
    <Wrapper>
      <BodyWrapper>
        <RejectCommentDrawer
          visible={visibleDrawer === DrawerType.RejectComment}
          onClose={() => {
            setVisibleDrawer(undefined);
          }}
          rejectNotes={rejectNotes}
        />
        <AddAdHocDrawer
          visible={visibleDrawer === DrawerType.AdhocTaskDrawer}
          startDate={startDate}
          projects={projects ?? []}
          timeEntryCategories={timeEntryCategories ?? []}
          onClose={() => {
            setVisibleDrawer(undefined);
          }}
          alreadyAddedAdhocTasks={alreadyAddedAdhocTasks}
          onAdhocTasksAdd={(adhocTasks) => {
            addAdhocActivitiesToTimesheet(adhocTasks);
            scrollToLastRow();
          }}
        />
        <AddTasksDrawer
          projects={projects ?? []}
          startDate={startDate}
          visible={visibleDrawer === DrawerType.TaskDrawer}
          alreadyAddedTasks={alreadyAddedTasks}
          onAddTasks={(taskList) => {
            addTasksToTimesheet(taskList);
            scrollToLastRow();
          }}
          onClose={() => {
            setVisibleDrawer(undefined);
          }}
          timesheetEntriesConfig={configuration.enabledSourceTypes}
        />
        <EditAdHocDrawer
          visible={visibleDrawer === DrawerType.EditAdhocTaskDrawer}
          editDetails={editDetails}
          editAdhocTaskName={editAdhocTaskName}
          onClose={() => {
            setVisibleDrawer(undefined);
            setEditDetails(undefined);
          }}
          userId={UserId(currentUser.userId)}
        />
        <CalenderDrawer
          visible={visibleDrawer === DrawerType.Integration}
          onClose={() => {
            setVisibleDrawer(undefined);
          }}
          startDate={startDate}
          projects={projects}
          addIntegrationTaskToTimesheet={addIntegrationTaskToTimesheet}
          timesheetWeekRows={timesheetWeekRows}
          configuration={configuration}
          timeEntryCategories={timeEntryCategories || []}
        />
        {isWeekFreezed && (
          <FreezeInfoNote isWeekFreezedAfter={isWeekFreezedAfter} />
        )}
        {isTimesheetLoading ? (
          <TableSkeleton columnCount={8} />
        ) : timesheetEntries.length === 0 && isWeekFreezed && !isUserAdmin ? (
          <EmptyTimesheet />
        ) : (
          <>
            {timesheet?.status === TimesheetStatus.Approved &&
            !isProjectOwnerApproval ? (
              <ApprovedSeal
                top={PAGE_HEADER_HEIGHT + EXTRA_SPACE}
                right={RIGHT_COMMENT_SPACE}
              />
            ) : null}
            <WeekViewTable
              startDate={startDate}
              workingDays={configuration.workingDays}
              data={timesheetWeekRows ?? []}
              editable={isInEditable}
              timeEntryCategories={timeEntryCategories ?? []}
              updateTimeEntryInTask={updateTimeEntryInTask}
              addTimeEntryInTask={addTimeEntryInTask}
              deleteTimeEntryInTask={deleteTimeEntryInTask}
              deleteTask={deleteTask}
              unsubmitTask={unsubmitTask}
              pageHeaderHeight={PAGE_HEADER_HEIGHT}
              setVisibleDrawer={setVisibleDrawer}
              setEditDetails={setEditDetails}
              addEntityToProject={addEntityToProject}
              timesheetEntriesConfig={configuration.enabledSourceTypes}
              approvalType={configuration.approvalSetting}
              lockSettings={configuration.lockSetting}
              projects={projects}
              projectOptions={projectOptions}
              changeProject={changeProject}
              addActivityToProject={addActivityToProject}
              mandateCategory={configuration.configMeta.categoryMandatory}
            />
            {(timesheet?.status !== TimesheetStatus.Approved ||
              isProjectOwnerApproval) &&
            (!isWeekFreezed || isUserAdmin) ? (
              <AddProjectActionRow
                onSelection={onSelection}
                projectOptions={projectOptions}
              />
            ) : null}
          </>
        )}
      </BodyWrapper>
      {!isTimesheetLoading ? (
        <Footer>
          {!(
            timesheetEntries.length === 0 &&
            isWeekFreezed &&
            !isUserAdmin
          ) && (
            <FooterTable
              startDate={startDate}
              timeSheetSummary={timeSheetSummary}
            />
          )}
          <StyledFooterActionRow>
            {!(isWeekFreezed && !isUserAdmin) ? (
              <>
                <div>
                  {isCurrentWeek &&
                  !(timesheet?.status === TimesheetStatus.Approved) ? (
                    <BlueGhostButton
                      data-cy="timesheets.buttons.add_tasks_from_last_week"
                      onClick={addPreviousWeektasks}
                      icon={<Copy16 />}
                    >
                      <FormattedMessage id="timesheets.buttons.add_tasks_from_last_week" />
                    </BlueGhostButton>
                  ) : null}
                </div>
                {timesheet?.status === TimesheetStatus.Submitted ? (
                  <BodyShort01Styled>
                    <FormattedMessage id="timesheets.text.submission_hint" />
                  </BodyShort01Styled>
                ) : null}
                {timesheet?.status === TimesheetStatus.Rejected ? (
                  <RejectInfo />
                ) : null}
                {timesheetEntries.length === 0 ? null : (
                  <>
                    {submitTimesheetCondition ? (
                      <SubmitTimesheetButton
                        timesheetEntries={timesheetEntries}
                        submitTimesheet={submitTimesheet}
                        timesheetStatusChangeInProgress={
                          isStatusChangeInProgress
                        }
                        submissionHoursConfiguration={
                          configuration.configMeta.submissionHours
                        }
                      />
                    ) : !(
                        configuration.lockSetting.lock.enabled &&
                        timesheet?.status === TimesheetStatus.Approved
                      ) || isUserAdmin ? (
                      <WithdrawAllButtonWithModal
                        isStatusChangeInProgress={isStatusChangeInProgress}
                        setConfirmationPopupVisible={
                          setConfirmationPopupVisible
                        }
                        unsubmitTimesheet={unsubmitTimesheet}
                        confirmationPopupVisible={confirmationPopupVisible}
                      />
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </StyledFooterActionRow>
        </Footer>
      ) : null}
    </Wrapper>
  );
};
